import { useSize } from 'ahooks';
import { HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Button } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { sortBy } from 'lodash-es';

const lineHeight = 32;

const Container = styled.div`
  padding: 8px 0;
  background: var(--theme-fill-6);
`;

const Fold = styled.div<{ height: string }>`
  display: flex;
  align-items: flex-start;
  height: ${({ height }) => height};
  overflow: hidden;
  transition: all 0.2s;
  > div {
    flex: 1 0 0;
  }
`;

const RegionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  line-height: ${lineHeight}px;
`;

const Region = styled.div<{ active: string }>`
  padding: 0 16px;
  color: ${({ active }) =>
    active === 'true' ? 'var(--ant-primary-color)' : 'var(--theme-text-2)'};
  cursor: pointer;
`;

export type RegionOption = General.UpperCaseRegion | 'ALL';

export interface RegionSelectorProps extends HTMLAttributes<HTMLDivElement> {
  activeRegion: RegionOption;
  onActiveRegionChange: (region: RegionOption) => void;
  regionList: RegionOption[];
  regionRender?: (region: RegionOption, regionLabel: string) => ReactNode;
}

export const defaultRegionRender = (_region: RegionOption, regionLabel: string) => regionLabel;

function RegionSelector(props: RegionSelectorProps) {
  const {
    activeRegion,
    onActiveRegionChange,
    regionList,
    regionRender = defaultRegionRender,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const [fold, setFold] = useState(true);

  const regionListRef = useRef<HTMLDivElement>(null);

  const { height: regionListHeight } = useSize(regionListRef) || {};

  const refsCurrent = {
    regionList,
    onActiveRegionChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (activeRegion === 'TW') {
      const {
        current: { regionList, onActiveRegionChange },
      } = refs;

      onActiveRegionChange(regionList[0]);
    }
  }, [activeRegion]);

  return (
    <Container {...otherProps}>
      <Fold height={`${fold ? lineHeight : regionListHeight}px`}>
        <RegionList ref={regionListRef}>
          {sortBy(
            regionList.filter((_) => _ !== 'TW'),
            (region) => (region === 'ALL' ? -1 : 0),
          ).map((region) => {
            const active = region === activeRegion;

            const label = region === 'ALL' ? t('review.全部') : getRegionLabel(region);

            return (
              <Region
                key={region}
                active={String(active)}
                onClick={() => {
                  if (!active) {
                    onActiveRegionChange(region);
                  }
                }}
              >
                {regionRender(region, label)}
              </Region>
            );
          })}
        </RegionList>
        {regionListHeight && regionListHeight > lineHeight ? (
          <Button type="link" onClick={() => setFold((fold) => !fold)}>
            {fold ? t('phrase.展开') : t('phrase.收起')}
            {fold ? <DownOutlined /> : <UpOutlined />}
          </Button>
        ) : null}
      </Fold>
    </Container>
  );
}

export default RegionSelector;
